// GameStateContext.js
import React, { createContext, useReducer, useContext, useEffect, useCallback, useRef } from 'react';
import { Chess } from 'chess.js';
import { createStartPosition } from '../../util/PositionData';

// MOCK ONLY: Replace with real Stockfish provider later
// Use useCallback to create a stable function reference
const useChessAnalysis = () => {
  const updatePositionSilently = useCallback((fen) => {
    // Mock implementation - intentionally do nothing
    
  }, []);
  
  return {
    updatePositionSilently
  };
};

const GameStateContext = createContext();


const initialState = {
  moves: [],
  currentMove: '-1',
  rootFEN: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  orientation: 'white',        // white POV by default
  starredMoves: [],            // empty initially
  initialFEN: null,  // Store the initial FEN if different from standard
  currentFEN: '',
  currentEval: null,
  skipPositionUpdate: false, // Add this flag to prevent position updates during analysis
  preservedPosition: null, // Store position FEN for recovery
  startPosition: {
    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    content: '',
    arrows: [],
    highlightedSquares: {},
    analysis: { pvs: [], scores: [], classification: '', depth: 0 },
    variations: []
  }, // New field for start position data
  isInitializing: false, // Flag to prevent rapid re-initialization
};

/**
 * Computes the move number based on index
 * @param {number} index - Index in the moves array
 * @returns {number} - The full move number (starting from 1)
 */
function computeMoveNumber(index) {
  return Math.floor(index / 2) + 1;
}

/**
 * Creates a fully structured move object
 * @param {string} notation - The chess notation
 * @param {number} index - Index in the moves array
 * @param {string} from - Source square (optional)
 * @param {string} to - Target square (optional)
 * @param {Object} additionalData - Additional data including creator info
 * @returns {Object} - A complete move object
 */
function createEnhancedMove(notation, index, from = '', to = '', additionalData = {}) {
  // Get the FEN position if provided, otherwise it will be computed later
  const fen = additionalData.fen || null;
  
  // Create the base move object with all required fields
  const move = {
    notation,
    from: from || additionalData.startSquare || '',
    to: to || additionalData.endSquare || '',
    moveNum: computeMoveNumber(index),
    content: additionalData.content || "",
    arrows: additionalData.arrows || [],
    highlightedSquares: additionalData.highlightedSquares || {},
    opening: null,
    fen: fen, // Store the FEN position explicitly for each move
    startSquare: from || additionalData.startSquare || '',
    endSquare: to || additionalData.endSquare || '',
    analysis: {
      pvs: [],
      scores: [],
      classification: "",
      depth: 0
    },
    ...(additionalData.analysis && { analysis: additionalData.analysis }), // Override analysis if provided
    ...(additionalData.content && { content: additionalData.content }) // Override content if provided
  };
  
  // Add creator metadata if provided (for comment moves)
  if (additionalData.creator) {
    move.creator = additionalData.creator;
    
  } else {
    
  }
  
  return move;
}


/**
 * Ensures a move object has all required fields
 * @param {Object} move - The move object to normalize
 * @param {number} index - Index in the moves array
 * @returns {Object} - A normalized move object
 */
function normalizeMove(move, index) {
  return {
    ...move,
    from: move.from || '',
    to: move.to || '',
    moveNum: move.moveNum || computeMoveNumber(index),
    content: move.content || "",
    arrows: move.arrows || [],
    highlightedSquares: move.highlightedSquares || {},
    opening: move.opening || null,
    analysis: move.analysis || {
      pvs: [],
      scores: [],
      classification: "",
      depth: 0
    }
  };
}

/**
 * Helper function to update a variation at any depth in the move tree
 * @param {Array} moves - Root moves array
 * @param {Array} pathParts - Path segments (e.g., ['4', '0:1', '1:0'])
 * @param {Function} updateFunction - Function to apply to the target node
 * @returns {Object} Result with success flag and updated moves
 */
function updateVariationAtDeepPath(moves, pathParts, updateFunction) {
  try {
    if (!pathParts || pathParts.length === 0) {
      return { success: false, moves };
    }
    
    const newMoves = [...moves];
    let currentArray = newMoves;
    let currentNode = null;
    let navigationPath = [];
    
    // Navigate to the target position
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      
      if (i === 0) {
        // Main line index
        const idx = parseInt(part, 10);
        if (idx < 0 || idx >= currentArray.length) {
          return { success: false, moves };
        }
        
        currentNode = currentArray[idx];
        navigationPath.push({ type: 'main', index: idx, array: currentArray });
        
        // If this is the last part, apply the update function
        if (i === pathParts.length - 1) {
          currentArray[idx] = updateFunction(currentNode);
          return { success: true, moves: newMoves };
        }
        
      } else {
        // Variation path (format: "varIdx:moveIdx")
        if (!part.includes(':')) {
          return { success: false, moves };
        }
        
        const [varIdx, moveIdx] = part.split(':').map(Number);
        
        if (!currentNode.variations || varIdx < 0 || varIdx >= currentNode.variations.length) {
          return { success: false, moves };
        }
        
        const variation = currentNode.variations[varIdx];
        if (moveIdx < 0 || moveIdx >= variation.length) {
          return { success: false, moves };
        }
        
        // Create a copy of the variation for updating
        const newVariation = [...variation];
        const newVariations = [...currentNode.variations];
        newVariations[varIdx] = newVariation;
        
        // Update the current node to point to the new variation
        const navItem = navigationPath[navigationPath.length - 1];
        if (navItem.type === 'main') {
          navItem.array[navItem.index] = {
            ...currentNode,
            variations: newVariations
          };
        } else {
          navItem.array[navItem.moveIndex] = {
            ...currentNode,
            variations: newVariations
          };
        }
        
        currentArray = newVariation;
        currentNode = newVariation[moveIdx];
        navigationPath.push({ 
          type: 'variation', 
          varIndex: varIdx, 
          moveIndex: moveIdx, 
          array: newVariation 
        });
        
        // If this is the last part, apply the update function
        if (i === pathParts.length - 1) {
          newVariation[moveIdx] = updateFunction(currentNode);
          return { success: true, moves: newMoves };
        }
      }
    }
    
    return { success: false, moves };
    
  } catch (error) {
    console.error('[GameStateContext] updateVariationAtDeepPath error:', error);
    return { success: false, moves };
  }
}

function reducer(state, action) {
  if (action.type === 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS') {
    // console.log('🔍 [GameStateContext] Reducer ENTRY - UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS');
  }
  
  switch (action.type) {
    case 'INITIALIZE_GAME':
      // Single action to initialize all game state at once
      // This helps prevent multiple re-renders from sequential dispatches
      
      
      // Prevent rapid re-initialization if we're currently initializing
      if (state.isInitializing) {
        
        return state;
      }
      
      // Ensure we have valid arrays before mapping
      const moves = action.payload?.moves || [];
      const starredMoves = action.payload?.starredMoves || [];
      
      // Safely normalize moves
      const initNormalizedMoves = moves.map((move, index) => normalizeMove(move, index));
      
      const initFEN = action.payload?.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      
      // Skip if this initialization would not change anything significant
      // BUT: Always allow FEN changes for mobile FEN builder
      const shouldSkip = state.initialFEN === initFEN && 
          state.moves.length === initNormalizedMoves.length &&
          state.currentMove === '-1' &&
          state.currentFEN === initFEN;
          
      if (shouldSkip) {
        // console.log('🔥 [GameStateContext] INITIALIZE_GAME skipped - no changes needed:', {
        //   currentInitialFEN: state.initialFEN,
        //   newInitialFEN: initFEN,
        //   currentMovesLength: state.moves.length,
        //   newMovesLength: initNormalizedMoves.length,
        //   currentMove: state.currentMove,
        //   currentFEN: state.currentFEN,
        //   source: 'INITIALIZE_GAME-skip'
        // });
        return state;
      }
      
      const newState = {
        ...state,
        rootFEN: initFEN,
        initialFEN: initFEN,
        orientation: action.payload?.orientation || 'white',
        moves: initNormalizedMoves,
        starredMoves: starredMoves,
        currentMove: '-1',
        currentFEN: initFEN,
        startPosition: action.payload?.startPosition || createStartPosition(initFEN),
        isInitializing: false // Reset the initialization flag
      };
      
      // console.log('🔥 [GameStateContext] INITIALIZE_GAME processed:', {
      //   inputFEN: initFEN,
      //   outputCurrentFEN: newState.currentFEN,
      //   outputInitialFEN: newState.initialFEN,
      //   outputRootFEN: newState.rootFEN,
      //   movesLength: initNormalizedMoves.length,
      //   source: 'INITIALIZE_GAME-reducer'
      // });
      
      return newState;

    case 'SET_MOVES':
      // Normalize all moves to ensure they have the complete structure
      const normalizedMoves = (action.payload || []).map((move, index) => {
        const normalized = normalizeMove(move, index);
        
        // Log if we're potentially losing opening data during normalization
        if (move.opening && !normalized.opening) {
          console.error('📖 [GameStateContext] LOST opening data during normalization:', {
            original: move.opening,
            normalized: normalized.opening,
            moveIndex: index,
            moveNotation: move.notation
          });
        } else if (move.opening) {
          
        }
        
        return normalized;
      });
      const initialPosition = state.initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      
      
      
      
      return {
        ...state,
        moves: normalizedMoves,
        currentMove: '-1',
        currentFEN: initialPosition // Use explicit FEN instead of creating new Chess
      };

    case 'UPDATE_MOVE_CONTENT': {
      const { moveId, content } = action.payload;
      
      // Don't change anything for invalid moveId
      if (moveId === '-1' || !state.moves.length) {
        return state;
      }
      
      // Create a copy of moves
      const updatedMoves = [...state.moves];
      
      // Handle main line move
      if (!moveId?.includes('|')) {
        const idx = parseInt(moveId, 10);
        
        if (idx >= 0 && idx < updatedMoves.length) {
          // Only update content field, preserve all other move properties
          updatedMoves[idx] = {
            ...updatedMoves[idx],
            content
          };
          
          
          
          return {
            ...state,
            moves: updatedMoves
            // Don't update currentMove or currentFEN to prevent board reset
          };
        }
      } else {
        // Handle variation move content updates
        // Parse the variation path
        const parts = moveId.split('|');
        const mainMoveIdx = parseInt(parts[0], 10);
        
        // Ensure the main move exists
        if (mainMoveIdx >= 0 && mainMoveIdx < updatedMoves.length) {
          // Start at the main move
          let currentNode = updatedMoves[mainMoveIdx];
          let parentNodes = []; // Keep track of parent nodes for updating
          let pathIndices = []; // Keep track of path indices for updating
          
          // Navigate through the variation path
          let success = true;
          for (let i = 1; i < parts.length; i++) {
            // Get variation and move indices
            const [varIdx, moveIdx] = parts[i].split(':').map(Number);
            
            // Ensure variations exist at this node
            if (!currentNode.variations || !Array.isArray(currentNode.variations[varIdx])) {
              console.error(`Variation ${varIdx} not found at node`, currentNode);
              success = false;
              break;
            }
            
            const variation = currentNode.variations[varIdx];
            
            // Ensure the move exists in this variation
            if (moveIdx < 0 || moveIdx >= variation.length) {
              console.error(`Move ${moveIdx} not found in variation ${varIdx}`);
              success = false;
              break;
            }
            
            // If we're at the last part of the path, update the content
            if (i === parts.length - 1) {
              // Create updated variation with content change
              const updatedVariation = [...variation];
              updatedVariation[moveIdx] = {
                ...updatedVariation[moveIdx],
                content
              };
              
              // Update parent node's variations
              const updatedVariations = [...currentNode.variations];
              updatedVariations[varIdx] = updatedVariation;
              
              // If this is a direct child of the main move
              if (i === 1) {
                updatedMoves[mainMoveIdx] = {
                  ...updatedMoves[mainMoveIdx],
                  variations: updatedVariations
                };
              } else {
                // We need to update the parent nodes up the chain
                let updatedParent = {
                  ...parentNodes[parentNodes.length - 1],
                  variations: updatedVariations
                };
                
                // Traverse back up the path
                for (let j = parentNodes.length - 2; j >= 0; j--) {
                  const [pVarIdx, pMoveIdx] = pathIndices[j];
                  let parentVariation = [...parentNodes[j].variations[pVarIdx]];
                  parentVariation[pMoveIdx] = updatedParent;
                  
                  updatedParent = {
                    ...parentNodes[j],
                    variations: [
                      ...parentNodes[j].variations.slice(0, pVarIdx),
                      parentVariation,
                      ...parentNodes[j].variations.slice(pVarIdx + 1)
                    ]
                  };
                }
                
                // Finally update the main move
                updatedMoves[mainMoveIdx] = updatedParent;
              }
              
              
              break;
            }
            
            // If we need to continue deeper, store this node as a parent and move to the next node
            parentNodes.push(currentNode);
            pathIndices.push([varIdx, moveIdx]);
            currentNode = variation[moveIdx];
          }
          
          if (!success) {
            return state; // Return unchanged state if variation path was invalid
          }
          
          return {
            ...state,
            moves: updatedMoves
            // Don't update currentMove or currentFEN to prevent board reset
          };
        }
      }
      
      return state;
    }

    case 'UPDATE_VARIATION_CAPTION': {
      const { mainMoveId, variationIdx, content } = action.payload;
      
      // Validate inputs
      if (mainMoveId === undefined || variationIdx === undefined || !state.moves.length) {
        
        return state;
      }
      
      // Try to find the main move that contains the variation
      const idx = parseInt(mainMoveId, 10);
      if (isNaN(idx) || idx < 0 || idx >= state.moves.length) {
        
        return state;
      }
      
      // Get the move that contains the variation
      const mainMove = state.moves[idx];
      
      // Create a new move with updated variation captions
      const updatedMove = {
        ...mainMove,
        variationCaptions: {
          ...(mainMove.variationCaptions || {}),
          [variationIdx]: content
        }
      };
      
      // Create a new moves array with the updated move
      const updatedMoves = [...state.moves];
      updatedMoves[idx] = updatedMove;
      
      
      
      return {
        ...state,
        moves: updatedMoves
        // Don't update currentMove or currentFEN to prevent board reset
      };
    }

    case 'SET_ROOT_FEN':
      return { ...state, rootFEN: action.payload };

    case 'TOGGLE_STAR_MOVE': {
      const moveId = action.payload;
      
      // Ensure moveId is a string
      const moveIdString = String(moveId);
      
      
      
      
      // Check if this move ID is already in the starredMoves array
      const alreadyStarred = (state.starredMoves || []).includes(moveIdString);
      
      let updatedStarredMoves;
      if (alreadyStarred) {
        // Remove this move ID from starred moves
        updatedStarredMoves = (state.starredMoves || []).filter(id => id !== moveIdString);
      } else {
        // Add this move ID to starred moves
        updatedStarredMoves = [...(state.starredMoves || []), moveIdString];
      }
      
      
      
      return { ...state, starredMoves: updatedStarredMoves };
    }

    case 'SET_ORIENTATION':
      
      return { ...state, orientation: action.payload };

    case 'SET_INITIAL_POSITION':
      const newFEN = action.payload;
      const newStartPosition = newFEN ? createStartPosition(newFEN) : state.startPosition;
      
      return {
        ...state,
        initialFEN: newFEN,
        rootFEN: newFEN,
        currentFEN: newFEN,
        startPosition: newStartPosition
      };

    case 'UPDATE_START_POSITION':
      // Update properties of the start position
      return {
        ...state,
        startPosition: {
          ...state.startPosition,
          ...action.payload
        }
      };

    case 'SET_START_POSITION_COMPLETE':
      // Completely replace the start position (used for restoring saved state)
      return {
        ...state,
        startPosition: action.payload
      };

    case 'UPDATE_START_POSITION_ANALYSIS':
      // Specifically update analysis data for start position
      return {
        ...state,
        startPosition: {
          ...state.startPosition,
          analysis: action.payload
        }
      };

    case 'UPDATE_START_POSITION_CONTENT':
      // Update content for start position
      return {
        ...state,
        startPosition: {
          ...state.startPosition,
          content: action.payload
        }
      };

    case 'UPDATE_START_POSITION_VISUALS':
      // Update arrows and highlighted squares for start position
      // console.log('🔥 [GameStateContext] UPDATE_START_POSITION_VISUALS received:', {
      //   payload: action.payload,
      //   currentStartPosition: state.startPosition
      // });
      
      // Ensure startPosition exists with proper fallbacks
      const existingStartPosition = state.startPosition || {
        fen: state.initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        content: '',
        arrows: [],
        highlightedSquares: {},
        analysis: { pvs: [], scores: [], classification: "", depth: 0 },
        variations: []
      };
      
      const updatedStartPosition = {
        ...existingStartPosition,
        arrows: action.payload.arrows !== undefined ? action.payload.arrows : (existingStartPosition.arrows || []),
        highlightedSquares: action.payload.highlightedSquares !== undefined ? action.payload.highlightedSquares : (existingStartPosition.highlightedSquares || {})
      };
      
      // console.log('🔥 [GameStateContext] Updated startPosition:', updatedStartPosition);
      
      return {
        ...state,
        startPosition: updatedStartPosition
      };

    case 'UPDATE_START_POSITION_VARIATION_CAPTION':
      // Update variation caption for start position variation
      return {
        ...state,
        startPosition: {
          ...state.startPosition,
          variationCaptions: {
            ...(state.startPosition?.variationCaptions || {}),
            [action.payload.variationIdx]: action.payload.content
          }
        }
      };

    case 'UPDATE_START_POSITION_VARIATION_MOVE_CONTENT':
      // Update content for a specific move in a start position variation
      return {
        ...state,
        startPosition: {
          ...state.startPosition,
          variations: state.startPosition.variations.map((variation, varIdx) => {
            if (varIdx === action.payload.variationIdx) {
              return variation.map((move, moveIdx) => {
                if (moveIdx === action.payload.moveIdx) {
                  return {
                    ...move,
                    content: action.payload.content
                  };
                }
                return move;
              });
            }
            return variation;
          })
        }
      };

    case 'UPDATE_MOVE_VARIATION_CONTENT':
      // Update content for a specific move in a regular move variation
      const { mainMoveIdx, variationIdx, moveIdx, content } = action.payload;
      
      return {
        ...state,
        moves: state.moves.map((move, index) => {
          if (index === mainMoveIdx) {
            return {
              ...move,
              variations: move.variations ? move.variations.map((variation, varIdx) => {
                if (varIdx === variationIdx) {
                  return variation.map((varMove, varMoveIdx) => {
                    if (varMoveIdx === moveIdx) {
                      return {
                        ...varMove,
                        content: content
                      };
                    }
                    return varMove;
                  });
                }
                return variation;
              }) : []
            };
          }
          return move;
        })
      };

    case 'UPDATE_NESTED_VARIATION_CONTENT': {
      // Update content for nested variations using the full path
      const { path, content } = action.payload;
      
      if (!path || !path?.includes('|')) {
        console.error('[GameStateContext] UPDATE_NESTED_VARIATION_CONTENT requires a valid variation path');
        return state;
      }

      // Parse the variation path to navigate to the correct nested location
      const parts = path.split('|');
      
      if (parts.length < 3) {
        // Not a nested variation, delegate to regular action
        console.warn('[GameStateContext] Path not nested, use UPDATE_MOVE_VARIATION_CONTENT instead');
        return state;
      }

      const mainMoveIdx = parseInt(parts[0]);
      
      // Ensure main move exists
      if (mainMoveIdx < 0 || mainMoveIdx >= state.moves.length) {
        console.error('[GameStateContext] Invalid main move index:', mainMoveIdx);
        return state;
      }

      const updatedMoves = [...state.moves];
      let currentNode = updatedMoves[mainMoveIdx];
      let updatePath = [];
      
      // Navigate through the nested path
      for (let i = 1; i < parts.length; i++) {
        const [varIdx, moveIdx] = parts[i].split(':').map(Number);
        
        if (!currentNode.variations || !currentNode.variations[varIdx] || moveIdx >= currentNode.variations[varIdx].length) {
          console.error('[GameStateContext] Invalid variation path at segment:', parts[i]);
          return state;
        }
        
        updatePath.push({ varIdx, moveIdx });
        
        if (i === parts.length - 1) {
          // This is the target move - update its content
          const targetVariation = [...currentNode.variations[varIdx]];
          targetVariation[moveIdx] = {
            ...targetVariation[moveIdx],
            content: content
          };
          
          // Update the variation array
          const updatedVariations = [...currentNode.variations];
          updatedVariations[varIdx] = targetVariation;
          currentNode.variations = updatedVariations;
        } else {
          // Navigate deeper
          currentNode = currentNode.variations[varIdx][moveIdx];
        }
      }
      
      return {
        ...state,
        moves: updatedMoves
      };
    }

    case 'GOTO_MOVE': {
      const payload = action.payload;
      
      // Validate the payload before setting currentMove
      if (payload === null || payload === undefined) {
        console.warn('GameStateContext - GOTO_MOVE: Invalid payload (null/undefined), ignoring');
        return state;
      }
      
      // Convert to string and validate
      const newMove = String(payload);
      
      // Check for invalid string values
      if (newMove === '' || newMove === 'null' || newMove === 'undefined') {
        console.warn('GameStateContext - GOTO_MOVE: Invalid move string:', newMove, 'ignoring');
        return state;
      }
      
      
      return { ...state, currentMove: newMove };
    }
    
    case 'SET_FEN': {
      const newFEN = action.payload;
      
      
      // Prevent unnecessary state updates if FEN hasn't changed
      if (newFEN === state.currentFEN) {
        
        return state;
      }
      
      return {
        ...state,
        currentFEN: newFEN
      };
    }

    case 'ADD_MOVE': {
      // Add a move to the end of the main line
      const newmoves = [...state.moves];
      
      // If we're not at the end, truncate the move list
      if (state.currentMove !== '-1' && state.currentMove && !state.currentMove.includes('|')) {
        const moveIndex = parseInt(state.currentMove, 10);
        newmoves.splice(moveIndex + 1); // Remove moves after the current one
      }
      
      // Extract move notation, square information, and any additional data
      const { notation, from = '', to = '', ...additionalData } = action.payload;
      
      // Add the new move with the enhanced structure
      const newIndex = newmoves.length;
      newmoves.push(createEnhancedMove(notation, newIndex, from, to, additionalData));
      
      return {
        ...state,
        moves: newmoves,
        currentMove: String(newmoves.length - 1)
      };
    }

    case 'ADD_VARIATION': {
      // Add a new variation to a move
      const { parentIdx, variation } = action.payload;
      const newmoves = [...state.moves];
      
      if (parentIdx >= 0 && parentIdx < newmoves.length) {
        const parentMove = { ...newmoves[parentIdx] };
        
        // Process each move in the variation with its additional data
        const enhancedVariation = variation.map((move, index) => {
          const { notation, from = '', to = '', ...additionalData } = move;
          return createEnhancedMove(notation, index, from, to, additionalData);
        });
        
        parentMove.variations = [...(parentMove.variations || []), enhancedVariation];
        newmoves[parentIdx] = parentMove;
      }
      
      return {
        ...state,
        moves: newmoves
      };
    }

    case 'ADD_VARIATION_MOVE': {
      // Add a move to an existing variation
      const { path, notation, from = '', to = '', ...additionalData } = action.payload;
      
      if (!path?.includes('|')) return state; // Not a variation
      
      const parts = path.split('|');
      const mainIdx = parseInt(parts[0], 10);
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      
      // Handle starting position variations
      if (mainIdx === -1) {
        const newState = { ...state };
        
        // Ensure startPosition exists
        if (!newState.startPosition) {
          newState.startPosition = {
            fen: state.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
            content: '',
            arrows: [],
            highlightedSquares: {},
            analysis: { pvs: [], scores: [], classification: '', depth: 0 },
            variations: []
          };
        }
        
        // Ensure variations array exists
        if (!newState.startPosition.variations) {
          newState.startPosition.variations = [];
        }
        
        // Add move to the starting position variation
        if (varIdx < newState.startPosition.variations.length) {
          const variation = [...newState.startPosition.variations[varIdx]];
          
          // Calculate the FEN for this starting position variation move
          const chess = new Chess(newState.startPosition.fen || newState.initialFEN);
          
          // Apply all previous moves in this variation
          for (let i = 0; i < variation.length; i++) {
            try {
              chess.move(variation[i].notation);
            } catch (err) {
              console.error('Error applying previous variation move:', err);
              break;
            }
          }
          
          // Apply the new move to get the FEN
          let newMoveFEN = newState.startPosition.fen || newState.initialFEN;
          try {
            const moveResult = chess.move(notation);
            if (moveResult) {
              newMoveFEN = chess.fen();
            }
          } catch (err) {
            console.error('Error calculating FEN for new starting position variation move:', err);
          }
          
          const newMove = createEnhancedMove(notation, variation.length, from, to, {
            ...additionalData,
            fen: newMoveFEN // Store the calculated FEN
          });
          variation.push(newMove);
          newState.startPosition.variations[varIdx] = variation;
          
          // console.log('✅ Added starting position variation move with FEN:', {
          //   notation,
          //   path,
          //   calculatedFEN: newMoveFEN,
          //   variationLength: variation.length
          // });
        }
        
        return newState;
      }
      
      const newmoves = [...state.moves];
      
      // Traverse to find the right variation
      let currentNode = newmoves[mainIdx];
      
      for (let i = 1; i < parts.length - 1; i++) {
        if (!parts[i].includes(':')) continue;
        
        const [vIdx, mIdx] = parts[i].split(':').map(Number);
        if (!currentNode.variations?.[vIdx]?.[mIdx]) return state;
        
        currentNode = currentNode.variations[vIdx][mIdx];
      }
      
      // Add the move to the variation
      if (!currentNode.variations?.[varIdx]) return state;
      
      const variation = [...currentNode.variations[varIdx]];
      
      // Truncate variation if needed
      variation.splice(moveIdx + 1);
      
      // Add the new move with enhanced structure
      const newIndex = variation.length;
      variation.push(createEnhancedMove(notation, newIndex, from, to, additionalData));
      
      currentNode.variations[varIdx] = variation;
      
      return {
        ...state,
        moves: newmoves
      };
    }

    case 'SET_EVAL':
      return { ...state, currentEval: action.payload };

    case 'ADD_VARIATION_AT_PATH': {
      // Enhanced action to add variations at any depth using path system
      const { path, variation } = action.payload;
      
      if (!path) {
        console.error('[GameStateContext] ADD_VARIATION_AT_PATH: No path provided');
        return state;
      }
      
      // Handle starting position variations
      if (path === '-1') {
        const newState = { ...state };
        
        // Ensure startPosition exists
        if (!newState.startPosition) {
          newState.startPosition = {
            fen: state.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
            content: '',
            arrows: [],
            highlightedSquares: {},
            analysis: { pvs: [], scores: [], classification: '', depth: 0 },
            variations: []
          };
        }
        
        // Ensure variations array exists
        if (!newState.startPosition.variations) {
          newState.startPosition.variations = [];
        }
        
        // Add the variation to starting position
        const enhancedVariation = variation.map((move, index) => {
          const { notation, from = '', to = '', ...additionalData } = move;
          return createEnhancedMove(notation, index, from, to, additionalData);
        });
        
        newState.startPosition.variations.push(enhancedVariation);
        return newState;
      }
      
      const newMoves = [...state.moves];
      const pathParts = path.split('|');
      
      // Navigate to the target node and add variation
      if (pathParts.length === 1) {
        // Adding variation to main line move
        const idx = parseInt(pathParts[0], 10);
        if (idx >= 0 && idx < newMoves.length) {
          const enhancedVariation = variation.map((move, index) => {
            const { notation, from = '', to = '', ...additionalData } = move;
            return createEnhancedMove(notation, index, from, to, additionalData);
          });
          
          newMoves[idx] = {
            ...newMoves[idx],
            variations: [...(newMoves[idx].variations || []), enhancedVariation]
          };
        }
      } else {
        // Adding variation to nested position - need to traverse tree
        try {
          const result = updateVariationAtDeepPath(newMoves, pathParts, (targetNode) => {
            const enhancedVariation = variation.map((move, index) => {
              const { notation, from = '', to = '', ...additionalData } = move;
              return createEnhancedMove(notation, index, from, to, additionalData);
            });
            
            return {
              ...targetNode,
              variations: [...(targetNode.variations || []), enhancedVariation]
            };
          });
          
          if (result.success) {
            return {
              ...state,
              moves: result.moves
            };
          }
        } catch (error) {
          console.error('[GameStateContext] ADD_VARIATION_AT_PATH: Error updating deep variation:', error);
        }
      }
      
      return {
        ...state,
        moves: newMoves
      };
    }

    case 'DELETE_VARIATION_AT_PATH': {
      // Delete a variation at any depth
      const { path, variationIndex } = action.payload;
      
      if (!path || path === '-1') {
        return state;
      }
      
      const newMoves = [...state.moves];
      const pathParts = path.split('|');
      
      if (pathParts.length === 1) {
        // Deleting variation from main line move
        const idx = parseInt(pathParts[0], 10);
        if (idx >= 0 && idx < newMoves.length && newMoves[idx].variations) {
          const variations = [...newMoves[idx].variations];
          if (variationIndex >= 0 && variationIndex < variations.length) {
            variations.splice(variationIndex, 1);
            newMoves[idx] = {
              ...newMoves[idx],
              variations: variations
            };
          }
        }
      } else {
        // Deleting variation from nested position
        try {
          const result = updateVariationAtDeepPath(newMoves, pathParts, (targetNode) => {
            if (!targetNode.variations || variationIndex < 0 || variationIndex >= targetNode.variations.length) {
              return targetNode;
            }
            
            const variations = [...targetNode.variations];
            variations.splice(variationIndex, 1);
            
            return {
              ...targetNode,
              variations: variations
            };
          });
          
          if (result.success) {
            return {
              ...state,
              moves: result.moves
            };
          }
        } catch (error) {
          console.error('[GameStateContext] DELETE_VARIATION_AT_PATH: Error deleting deep variation:', error);
        }
      }
      
      return {
        ...state,
        moves: newMoves
      };
    }

    case 'UPDATE_MOVE_PROPERTIES_AT_PATH': {
      // Update properties of a move at any depth
      const { path, properties } = action.payload;
      
      if (!path || path === '-1') {
        // Update start position
        return {
          ...state,
          startPosition: {
            ...state.startPosition,
            ...properties
          }
        };
      }
      
      const newMoves = [...state.moves];
      const pathParts = path.split('|');
      
      if (pathParts.length === 1) {
        // Updating main line move
        const idx = parseInt(pathParts[0], 10);
        if (idx >= 0 && idx < newMoves.length) {
          newMoves[idx] = {
            ...newMoves[idx],
            ...properties
          };
        }
      } else {
        // Updating nested move
        try {
          const result = updateVariationAtDeepPath(newMoves, pathParts, (targetNode) => ({
            ...targetNode,
            ...properties
          }));
          
          if (result.success) {
            return {
              ...state,
              moves: result.moves
            };
          }
        } catch (error) {
          console.error('[GameStateContext] UPDATE_MOVE_PROPERTIES_AT_PATH: Error updating deep move:', error);
        }
      }
      
      return {
        ...state,
        moves: newMoves
      };
    }

    case 'UPDATE_VARIATIONS':
      const { path, variations } = action.payload;
      const pathParts = path.split('|');
      
      // If we're updating variations at the root level
      if (pathParts.length === 1) {
        const idx = parseInt(pathParts[0], 10);
        if (idx >= 0 && idx < state.moves.length) {
          const newmoves = [...state.moves];
          newmoves[idx] = {
            ...newmoves[idx],
            variations
          };
          return {
            ...state,
            moves: newmoves
          };
        }
      }
      
      // For deeper paths, we need to traverse the tree
      const updateVariationsInTree = (moves, pathParts, variations) => {
        if (pathParts.length === 0) return moves;
        
        const newmoves = [...moves];
        const currentPart = pathParts[0];
        
        if (pathParts.length === 1) {
          // We're at the target node
          const idx = parseInt(currentPart, 10);
          if (idx >= 0 && idx < newmoves.length) {
            newmoves[idx] = {
              ...newmoves[idx],
              variations
            };
          }
        } else {
          // Continue traversing
          const idx = parseInt(currentPart, 10);
          if (idx >= 0 && idx < newmoves.length) {
            const [varIdx, moveIdx] = pathParts[1].split(':').map(Number);
            if (newmoves[idx].variations?.[varIdx]) {
              newmoves[idx].variations[varIdx] = updateVariationsInTree(
                newmoves[idx].variations[varIdx],
                pathParts.slice(1),
                variations
              );
            }
          }
        }
        
        return newmoves;
      };
      
      return {
        ...state,
        moves: updateVariationsInTree(state.moves, pathParts, variations)
      };

    case 'UPDATE_MOVE_PROPERTIES': {
      // Update properties (arrows, highlightedSquares, etc.) for an existing move
      // without creating a new move or changing positions
      const { moveId, properties } = action.payload;
      
      if (!moveId || !properties || Object.keys(properties).length === 0) {
        return state;
      }
      
      
      
      // Special logging for opening data updates
      if (properties.opening) {
        
      }
      
      // Create a copy of moves
      const updatedMoves = [...state.moves];
      
      // Handle main line move first (simpler case)
      if (!moveId?.includes('|')) {
        const idx = parseInt(moveId, 10);
        if (idx >= 0 && idx < updatedMoves.length) {
          // Only update the specified properties, preserving the rest
          updatedMoves[idx] = {
            ...updatedMoves[idx],
            ...properties
          };
          
          
          
          // Special verification for opening data
          if (properties.opening) {
            
          }
          
          return {
            ...state,
            moves: updatedMoves
          };
        }
      } else {
        // For variations, we need to traverse the move tree
        const parts = moveId.split('|');
        const mainIdx = parseInt(parts[0], 10);
        
        
        
        // First ensure main index is valid
        if (mainIdx < 0 || mainIdx >= updatedMoves.length) {
          console.error('[GameStateContext] Invalid main index for variation:', mainIdx);
          return state;
        }
        
        // Simple case: direct child variation of a main line move
        if (parts.length === 2) {
          const [varIdx, moveIdx] = parts[1].split(':').map(Number);
          const parentMove = updatedMoves[mainIdx];
          
          // Make sure the variation exists
          if (!parentMove.variations || !parentMove.variations[varIdx]) {
            console.error('[GameStateContext] Variation not found:', varIdx, 'in move:', mainIdx);
            return state;
          }
          
          // Make sure the move index in variation is valid
          if (moveIdx < 0 || moveIdx >= parentMove.variations[varIdx].length) {
            console.error('[GameStateContext] Invalid move index in variation:', moveIdx);
            return state;
          }
          
          // Create a copy of the variation
          const updatedVariation = [...parentMove.variations[varIdx]];
          
          // Update the properties in the move
          updatedVariation[moveIdx] = {
            ...updatedVariation[moveIdx],
            ...properties
          };
          
          // Update the parent move's variations
          const updatedVariations = [...parentMove.variations];
          updatedVariations[varIdx] = updatedVariation;
          
          // Update the parent move
          updatedMoves[mainIdx] = {
            ...parentMove,
            variations: updatedVariations
          };
          
          
          return {
            ...state,
            moves: updatedMoves
          };
        }
        
        // For deeper variations, implement recursive traversal
        if (parts.length > 2) {
          
          
          // Build a function to recursively traverse the variation tree
          const updateDeepVariation = (node, pathParts, properties, depth = 0) => {
            // Base case: no more path parts
            if (depth >= pathParts.length) {
              return node;
            }
            
            // Current path part
            const part = pathParts[depth];
            
            // If this is the main line part
            if (depth === 0) {
              const idx = parseInt(part, 10);
              if (idx < 0 || idx >= node.length) return node;
              
              // If this is the last part, update the properties
              if (depth === pathParts.length - 1) {
                const updatedNode = [...node];
                updatedNode[idx] = {
                  ...updatedNode[idx],
                  ...properties
                };
                return updatedNode;
              }
              
              // Otherwise continue traversing
              const newNode = [...node];
              newNode[idx] = {
                ...newNode[idx],
                variations: newNode[idx].variations ? 
                  updateDeepVariation(newNode[idx].variations, pathParts, properties, depth + 1) : 
                  []
              };
              return newNode;
            }
            
            // For variation parts
            if (part.includes(':')) {
              const [varIdx, moveIdx] = part.split(':').map(Number);
              
              // If this is the last part, update the properties
              if (depth === pathParts.length - 1) {
                const updatedNode = [...node];
                
                // Ensure the variation exists
                if (!updatedNode[varIdx] || moveIdx >= updatedNode[varIdx].length) {
                  console.error('[GameStateContext] Invalid variation index for deep update:', varIdx, moveIdx);
                  return node;
                }
                
                // Update the move within this variation
                updatedNode[varIdx] = [...updatedNode[varIdx]];
                updatedNode[varIdx][moveIdx] = {
                  ...updatedNode[varIdx][moveIdx],
                  ...properties
                };
                
                return updatedNode;
              }
              
              // Otherwise continue traversing to the next level
              const updatedNode = [...node];
              
              // Ensure the variation exists
              if (!updatedNode[varIdx] || moveIdx >= updatedNode[varIdx].length) {
                console.error('[GameStateContext] Invalid variation index for deep traversal:', varIdx, moveIdx);
                return node;
              }
              
              // Get the move within this variation
              const move = updatedNode[varIdx][moveIdx];
              
              // Create new variations array for this move if needed
              const updatedMove = {
                ...move,
                variations: move.variations ? 
                  updateDeepVariation(move.variations, pathParts, properties, depth + 1) : 
                  []
              };
              
              // Update the move within this variation
              updatedNode[varIdx] = [...updatedNode[varIdx]];
              updatedNode[varIdx][moveIdx] = updatedMove;
              
              return updatedNode;
            }
            
            return node;
          };
          
          try {
            const result = updateDeepVariation(updatedMoves, parts, properties);
            
            return {
              ...state,
              moves: result
            };
          } catch (error) {
            console.error('[GameStateContext] Error updating deep variation:', error);
            return state;
          }
        }
      }
      
      
      return state;
    }

    case 'UPDATE_MOVE_ANALYSIS': {
      // Get the move ID and analysis data from the payload
      const { moveId, analysisData } = action.payload;
      
      // Only update if we have valid data
      if (moveId === undefined || !analysisData) return state;
      
      // Convert moveId to number if it's a string
      const moveIndex = typeof moveId === 'string' ? parseInt(moveId) : moveId;
      
      // Ensure the move index is valid
      if (isNaN(moveIndex) || moveIndex < 0 || moveIndex >= state.moves.length) {
        console.warn('UPDATE_MOVE_ANALYSIS: Invalid move index', moveIndex);
        return state;
      }
      
      // Create an updated move with the analysis data
      const updatedMove = {
        ...state.moves[moveIndex],
        analysis: {
          ...analysisData,
        }
      };
      
      // Create a new moves array with the updated move
      const updatedMoves = [...state.moves];
      updatedMoves[moveIndex] = updatedMove;
      
      // Return updated state with skipPositionUpdate flag set
      return {
        ...state,
        moves: updatedMoves,
        skipPositionUpdate: true, // Set flag to prevent position updates
      };
    }
    
    case 'UPDATE_START_ANALYSIS': {
      // Store analysis data for the start position
      const analysisData = action.payload;
      
      if (!analysisData) return state;
      
      
      
      return {
        ...state,
        startAnalysis: {
          ...analysisData,
          isStartPosition: true
        }
      };
    }
    
    // Add a case to clear the skipPositionUpdate flag
    case 'CLEAR_SKIP_POSITION_UPDATE': {
      return {
        ...state,
        skipPositionUpdate: false,
      };
    }
    
    // Add a case to store the current position for recovery
    case 'STORE_POSITION': {
      return {
        ...state,
        preservedPosition: action.payload,
      };
    }
    
    // Add a case to recover the position if needed
    case 'RECOVER_POSITION': {
      if (!state.preservedPosition) return state;
      
      return {
        ...state,
        preservedPosition: null, // Clear the preserved position
      };
    }

    case 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS': {
      // Handle analysis storage for start position variation moves
      const { moveId, properties } = action.payload;
      
      // console.log(`🔍 UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS called:`, { moveId, properties });
      
      // Parse the start position variation move ID (format: -1|varIdx:moveIdx)
      const match = moveId.match(/^-1\|(\d+):(\d+)$/);
      if (!match) {
        console.error(`❌ Invalid start position variation move ID: ${moveId}`);
        return state;
      }
      
      const varIdx = parseInt(match[1], 10);
      const moveIdx = parseInt(match[2], 10);
      
      // VALIDATION CHECK
      // console.log('🔍 Validation check:', {
      //   hasStartPosition: !!state.startPosition,
      //   hasVariations: !!state.startPosition?.variations,
      //   variationsLength: state.startPosition?.variations?.length,
      //   varIdx,
      //   moveIdx
      // });
      
      // Ensure we have start position and variations structure
      if (!state.startPosition || !state.startPosition.variations || 
          varIdx >= state.startPosition.variations.length ||
          !state.startPosition.variations[varIdx] ||
          moveIdx >= state.startPosition.variations[varIdx].length) {
        console.error(`❌ Invalid start position variation path: ${moveId}`, {
          hasStartPosition: !!state.startPosition,
          hasVariations: !!state.startPosition?.variations,
          variationsLength: state.startPosition?.variations?.length,
          varIdx,
          moveIdx
        });
        return state;
      }
      
      // console.log('✅ Validation passed! Creating updated start position...');
      
      // Create updated start position with analysis
      const newStartPosition = { ...state.startPosition };
      newStartPosition.variations = [...newStartPosition.variations];
      newStartPosition.variations[varIdx] = [...newStartPosition.variations[varIdx]];
      
      // console.log('🔍 Before update:', {
      //   originalMove: newStartPosition.variations[varIdx][moveIdx],
      //   properties
      // });
      
      newStartPosition.variations[varIdx][moveIdx] = {
        ...newStartPosition.variations[varIdx][moveIdx],
        ...properties
      };
      
      // console.log('🔍 After update:', {
      //   updatedMove: newStartPosition.variations[varIdx][moveIdx]
      // });
      
      // console.log(`✅ Stored analysis for start position variation move ${moveId}`, {
      //   varIdx,
      //   moveIdx,
      //   properties,
      //   updatedMove: newStartPosition.variations[varIdx][moveIdx]
      // });
      
      return {
        ...state,
        startPosition: newStartPosition
      };
    }

    case 'TEST_DISPATCH':
      // console.log('🔍 [GameStateContext] TEST_DISPATCH case reached');
      return { ...state, testValue: action.payload };
      
    default:
      // console.log('🔍 [GameStateContext] Default case reached for action:', action.type);
      return state;
  }
}

export const GameStateProvider = ({ children }) => {
  const [state, rawDispatch] = useReducer(reducer, initialState);
  
  // Wrap dispatch to add logging only for our specific action
  const dispatch = useCallback((action) => {
    if (action.type === 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS') {
      // console.log('🔍 [GameStateContext] dispatch called with UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS:', action);
    }
    const result = rawDispatch(action);
    if (action.type === 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS') {
      // console.log('🔍 [GameStateContext] rawDispatch completed for UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS');
    }
    return result;
  }, [rawDispatch]);
  const { updatePositionSilently } = useChessAnalysis();
  
  // Add ref to track last processed FEN to prevent redundant calls
  const lastProcessedFENRef = useRef(null);

  // React to FEN changes and update Stockfish
  useEffect(() => {
    // Skip if no FEN or if it's the same as last processed
    if (!state.currentFEN || state.currentFEN === lastProcessedFENRef.current) {
      return;
    }
    
    // Update the last processed FEN
    lastProcessedFENRef.current = state.currentFEN;
    
    // Add a small delay to prevent rapid-fire calls during initialization
    const timer = setTimeout(() => {
      // Double-check that this FEN is still the current one
      if (state.currentFEN === lastProcessedFENRef.current) {
        updatePositionSilently(state.currentFEN);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [state.currentFEN, updatePositionSilently]);

  return (
    <GameStateContext.Provider value={{ state, dispatch }}>
      {children}
    </GameStateContext.Provider>
  );
};

export const useGameState = () => {
  const context = useContext(GameStateContext);
  return context;
};